<!-- src/App.vue -->
<template>
  <v-app>
    <!-- App Bar -->
    <v-app-bar app color="primary" dark>
      <!-- Mobile menu toggle -->
      <v-app-bar-nav-icon
          @click="drawer = !drawer"
          class="d-md-none"
      ></v-app-bar-nav-icon>

      <v-app-bar-title>
        <div class="d-flex align-center">
          <v-btn :to="{ path: '/' }" variant="text" class="mr-2">
            <v-icon icon="mdi-disc" class="mr-2" />
            SSDG - Tracker
          </v-btn>
        </div>
      </v-app-bar-title>

      <!-- Navigation buttons - only visible on larger screens -->
      <div class="d-none d-md-flex">
        <v-btn :to="{ path: `/competition/${defaultCompetitionId}` }" variant="text" class="mr-2">
          Competition
        </v-btn>
        <v-btn :to="{ path: `/leaderboard/${defaultCompetitionId}` }" variant="text" class="mr-2">
          Leaderboard
        </v-btn>
        <v-btn :to="{ path: `/groups/${defaultCompetitionId}` }" variant="text" class="mr-2">
          Groups
        </v-btn>
        <v-btn :to="{ path: `/display/${defaultCompetitionId}` }" variant="text" class="mr-2">
          Display
        </v-btn>
      </div>

      <v-spacer></v-spacer>

      <!-- Live update indicator -->
      <v-chip
          v-if="autoUpdateEnabled"
          size="small"
          color="success"
          class="mr-2"
          variant="outlined"
      >
        <v-icon
            start
            icon="mdi-sync"
            size="x-small"
            :class="{'rotate': autoUpdateEnabled}"
        ></v-icon>
        <span class="d-none d-sm-inline">Auto-update</span>
      </v-chip>

      <!-- Toggle auto-update button -->
      <v-btn
          icon
          variant="text"
          @click="toggleAutoUpdate"
          :color="autoUpdateEnabled ? 'success' : 'grey'"
          class="mr-2"
      >
        <v-icon>{{ autoUpdateEnabled ? 'mdi-sync' : 'mdi-sync-off' }}</v-icon>
      </v-btn>

      <!-- Manual refresh button -->
      <v-btn icon @click="refreshData">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-app-bar>

    <!-- Navigation Drawer for mobile -->
    <v-navigation-drawer
        v-model="drawer"
        temporary
        location="left"
        width="280"
    >
      <v-list>
        <v-list-item
            prepend-icon="mdi-home"
            title="Home"
            :to="{ path: '/' }"
            @click="drawer = false"
        ></v-list-item>

        <v-list-item
            prepend-icon="mdi-golf-tee"
            title="Competition"
            :to="{ path: `/competition/${defaultCompetitionId}` }"
            @click="drawer = false"
        ></v-list-item>

        <v-list-item
            prepend-icon="mdi-trophy"
            title="Leaderboard"
            :to="{ path: `/leaderboard/${defaultCompetitionId}` }"
            @click="drawer = false"
        ></v-list-item>

        <v-list-item
            prepend-icon="mdi-account-group"
            title="Groups"
            :to="{ path: `/groups/${defaultCompetitionId}` }"
            @click="drawer = false"
        ></v-list-item>

        <v-list-item
            prepend-icon="mdi-trophy"
            title="Display"
            :to="{ path: `/display/${defaultCompetitionId}` }"
            @click="drawer = false"
        ></v-list-item>
      </v-list>

      <template v-slot:append>
        <div class="pa-2">
          <v-btn
              block
              color="primary"
              @click="refreshData(); drawer = false"
              prepend-icon="mdi-refresh"
          >
            Refresh Data
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <v-main>
      <v-container fluid>
        <router-view></router-view>
      </v-container>
    </v-main>

    <v-footer app color="primary" dark>
      <div class="d-flex flex-column flex-sm-row justify-space-between align-center w-100">
        <div class="text-caption mb-2 mb-sm-0">
          Data from Disc Golf Metrix | Last updated: {{ lastUpdatedTime }}
          <span v-if="timeUntilNextUpdate !== null && autoUpdateEnabled">
            | Next update in {{ timeUntilNextUpdate }}s
          </span>
        </div>
        <div class="text-caption">
          SSDG - Tournament Tracker &copy; {{ new Date().getFullYear() }}
        </div>
      </div>
    </v-footer>
  </v-app>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { useCompetitionStore } from './store/modules/competition'
import config from './config'

export default {
  name: 'App',

  setup() {
    const competitionStore = useCompetitionStore()
    const drawer = ref(false)

    // Get default competition ID from config
    const defaultCompetitionId = config.api.competitionId

    // Format the last updated time
    const lastUpdatedTime = computed(() => {
      if (!competitionStore.lastUpdated) return '-'
      return new Date(competitionStore.lastUpdated).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
    })

    // Access timeUntilNextUpdate from the store
    const timeUntilNextUpdate = computed(() => competitionStore.timeUntilNextUpdate)

    // Auto-update enabled status
    const autoUpdateEnabled = computed(() => competitionStore.autoUpdateEnabled)

    // Set up auto-refresh and countdown
    let countdownInterval = null

    onMounted(() => {
      // Initial data fetch
      competitionStore.fetchData()

      // Set up auto-refresh
      competitionStore.setupAutoRefresh()

      // Set up countdown timer
      countdownInterval = setInterval(() => {
        if (competitionStore.timeUntilNextUpdate > 0 && competitionStore.autoUpdateEnabled) {
          competitionStore.decrementCountdown()
        }
      }, 1000)
    })

    onBeforeUnmount(() => {
      // Clean up intervals
      if (countdownInterval) {
        clearInterval(countdownInterval)
      }
      competitionStore.clearIntervals()
    })

    // Toggle auto-update
    const toggleAutoUpdate = () => {
      competitionStore.toggleAutoUpdate()
    }

    // Manual refresh function
    const refreshData = () => {
      competitionStore.manualRefresh()
      competitionStore.resetCountdown()
    }

    return {
      drawer,
      lastUpdatedTime,
      timeUntilNextUpdate,
      autoUpdateEnabled,
      toggleAutoUpdate,
      refreshData,
      defaultCompetitionId
    }
  }
}
</script>

<style>
.rotate {
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Adjust container padding for mobile */
@media (max-width: 600px) {
  .v-container {
    padding: 8px !important;
  }
}
</style>
